import { useLazyQuery } from '@apollo/client'
import {
    Check,
    Download,
    Edit,
    Email,
    ForwardToInbox,
    Google,
    LinkedIn,
    Phone,
} from '@mui/icons-material'
import {
    Box,
    CircularProgress,
    IconButton,
    Stack,
    Typography,
} from '@mui/material'
import { FC } from 'react'
import { GetResumeUrl } from 'recruiter/graphql/documents.gql'
import useIsMobile from 'shared/hooks/useIsMobile'

interface ActionsMenuProps {
    name: string
    linkedinUrl: string
    profileCompletion: number
    email: string
    phone: string
    handleEditClick: () => void
    handleResendRegistrationClick: () => void
    resumeId?: string
    lastCandidateLogin?: Date | null
}

const ActionsMenu: FC<ActionsMenuProps> = ({
    name,
    linkedinUrl,
    profileCompletion,
    email,
    phone,
    handleEditClick,
    handleResendRegistrationClick,
    resumeId,
    lastCandidateLogin,
}) => {
    const showResendRegistration = !lastCandidateLogin
    const isMobile = useIsMobile()
    const [getResumeUrl] = useLazyQuery(GetResumeUrl)

    const handleResumeClick = async () => {
        if (resumeId) {
            const { data } = await getResumeUrl({
                variables: { documentId: resumeId },
            })
            const link = data?.download?.url
            if (link) window.open(link, '_blank')
        }
    }

    return (
        <Stack direction="row" alignItems="center" gap={0.5}>
            {
                /* profile completion */
                !isMobile && (
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            marginX: 1.25,
                        }}
                        title="Profile Completion"
                    >
                        <CircularProgress
                            color="primary"
                            variant="determinate"
                            value={profileCompletion}
                            size={24}
                            thickness={4}
                            sx={{ position: 'absolute' }}
                        />
                        {profileCompletion === 100 ? (
                            <Check color="primary" sx={{ fontSize: 14 }} />
                        ) : (
                            <Typography variant="caption">
                                {profileCompletion}%
                            </Typography>
                        )}
                    </Box>
                )
            }
            {/* linkedIn */}
            {linkedinUrl && (
                <IconButton
                    href={`//${linkedinUrl.includes('//') ? linkedinUrl.split('//')[1] : linkedinUrl}`}
                    color="primary"
                    target="_blank"
                >
                    <LinkedIn />
                </IconButton>
            )}
            {/* google */}
            {
                <IconButton
                    color="primary"
                    href={`https://www.google.com/search?q=${encodeURI(name)}`}
                    target="_blank"
                >
                    <Google />
                </IconButton>
            }
            {resumeId && (
                <IconButton
                    color="primary"
                    onClick={handleResumeClick}
                    title="Download Resume"
                >
                    <Download />
                </IconButton>
            )}
            {!isMobile && showResendRegistration && (
                <IconButton
                    color="primary"
                    onClick={handleResendRegistrationClick}
                    title="Resend Portal Invitation"
                >
                    <ForwardToInbox />
                </IconButton>
            )}
            {!isMobile && (
                <IconButton
                    color="primary"
                    onClick={handleEditClick}
                    title="Edit Profile"
                >
                    <Edit />
                </IconButton>
            )}
            {
                /* email */
                !!isMobile && (
                    <IconButton color="primary" href={`mailto:${email}`}>
                        <Email />
                    </IconButton>
                )
            }
            {
                /* phone */
                !!isMobile && (
                    <IconButton color="primary" href={`tel:${phone}`}>
                        <Phone />
                    </IconButton>
                )
            }
        </Stack>
    )
}

export default ActionsMenu
